import {
  FindhelpProgram,
  FindhelpReferralRequest,
  UserInfo,
  UniteUSReferralRequest,
  Need,
} from '@src/api.types';
import { TMemberInfo } from '@repo/utils';
import { getMemberDetailsFromStorage } from '@src/utills/storage';
import { capitalizeSentenceFirstLetters } from '@repo/utils';
import { UniteUsOfficeLocations, UniteUsProgram } from '@repo/ui';

// Constants
const DO_NOT_CONTACT = 'do not contact';

/**
 * Given a list of service tag strings from a findhelp program object,
 *  return a list of the strings formatted into select option objects.
 */
export function findhelpServiceTagsIntoNeedSelectOptions(
  serviceTags: string[] | undefined
): SelectOption[] {
  return (serviceTags ?? []).map((tag) => {return {
    label: capitalizeSentenceFirstLetters(tag),
    value: tag,
  }});
}

export function findhelpServiceTagsIntoNeedSelectOptionsforUniteus(
  serviceTags: { id: string; label: string }[]
): SelectOption[] {
  return (serviceTags ?? []).map((tag) => {return {
    label: tag.label,
    value: tag.id,
  }});
}

/**
 * Used to filter out the selected primary need from the secondary needs options.
 */
export function filterOutPrimarySelected(
  selectedPrimaryNeed: string | undefined,
  options: SelectOption[]
): SelectOption[] {
  return selectedPrimaryNeed
    ? options.filter((option) => {return option.value !== selectedPrimaryNeed})
    : options;
}

export type ReferralFormData = {
  advocateFirstName: string;
  advocateLastName: string;
  advocateEmail: string;
  memberFirstName: string;
  memberLastName: string;
  memberEmail: string;
  memberPhone: string;
  contactPreferences: string[];
  selectedNeeds: {
    primaryNeeds: string[];
    secondaryNeeds: string[];
  };
  comment: string; // Not used
  consent: boolean;
  ConfirmPermission?: boolean;
  pennsylvaniaCoalitionConsent: boolean | null;
  memberId?:any;
};

export type SelectOption = {
  label: string;
  value: string | Need;
};

function formDataContactPrefsToRequestContactPrefs(prefs: string[]): string[] {
  return prefs.includes(DO_NOT_CONTACT) ? [] : prefs;
}

// Helper function to normalize primary needs to array format
function normalizePrimaryNeedsToArray(
  primaryNeeds: string | string[]
): string[] {
  return Array.isArray(primaryNeeds) ? primaryNeeds : [primaryNeeds];
}

// Helper function to find contact by channel
function findContactByChannel(
  nextSteps: any[] | undefined,
  channel: string
): string | undefined {
  return nextSteps?.find((step) => {return step.channel === channel})?.contact;
}

export function formDataToFindhelpReferralRequest(
  formData: ReferralFormData,
  programData: FindhelpProgram,
  memberInfo: TMemberInfo,
  loggedInAdvocate: UserInfo
): FindhelpReferralRequest {
  const contactPrefs = formDataContactPrefsToRequestContactPrefs(
    formData.contactPreferences
  );
  const memberDetails = getMemberDetailsFromStorage();
  const primaryNeed = normalizePrimaryNeedsToArray(
    formData.selectedNeeds.primaryNeeds
  );

  const phoneContact = findContactByChannel(programData.next_steps, 'phone');
  const phoneOfficeContact = findContactByChannel(
    programData.next_steps,
    'phone_office'
  );
  const emailContact = findContactByChannel(programData.next_steps, 'email');

  const firstOffice = programData.offices[0];

  return {
    programDetails: {
      programName: programData.name,
      programOrgName: programData.provider_name,
      programAddr1: firstOffice?.address1 ?? '',
      programAddr2: firstOffice?.address2 ?? null,
      programCity: firstOffice?.city ?? '',
      programState: firstOffice?.state ?? '',
      programZipcode: firstOffice?.postal ?? '',
      programPhone: phoneContact ?? phoneOfficeContact ?? '',
      programEmail: emailContact ?? '',
    },
    memberId: memberInfo.member_id,
    programId: programData.id,
    receiverEmail: formData.memberEmail,
    receiverFirstName: formData.memberFirstName,
    receiverLastName: formData.memberLastName,
    receiverPhone: formData.memberPhone,
    receiverDateOfBirth: memberInfo.DOB,
    receiverZip: memberInfo.zipCode,
    contactPreferences: contactPrefs,
    comment: formData.comment || null,
    senderEmail: formData.advocateEmail,
    caseId: memberDetails?.case_id ?? null,
    selectedNeeds: {
      primaryNeeds: primaryNeed,
      secondaryNeeds: formData.selectedNeeds.secondaryNeeds,
    },
    consumerId: memberInfo.ConsumerId ?? '',
    lineOfBusiness: memberInfo.lineOfBusiness,
    consent: formData.consent,
    senderSecureRoles: loggedInAdvocate.roles.join(', '),
    medSup: memberInfo.medSup ?? false,
    gender: memberInfo.gender,
    stateCode: memberInfo.address?.state,
    address: memberInfo.address,
    enterpriseIndividual: memberInfo.enterpriseIndividualIdentifier,
    coalition_consent: formData.pennsylvaniaCoalitionConsent,
  };
}

export function formDataToUniteUsReferralRequest(
  formData: ReferralFormData,
  programData: UniteUsProgram,
  memberInfo: TMemberInfo,
  loggedInAdvocate: UserInfo,
  needSelectOptions: SelectOption[],
  zipcode:string
): UniteUSReferralRequest {
  const contactPrefs = formDataContactPrefsToRequestContactPrefs(
    formData.contactPreferences
  );
  const memberDetails = getMemberDetailsFromStorage();
  const primaryNeed = normalizePrimaryNeedsToArray(
    formData.selectedNeeds.primaryNeeds
  );
  const closestLocation: UniteUsOfficeLocations =
    programData?.document?.locations.find((location) => {return location.is_closest}) ||
    programData?.document?.locations[0];

  // For UniteUs, primaryNeed is actually an object with label and id properties
  const needValue: any = primaryNeed[0];

  return {
    member: {
      nameFirst: memberInfo.first_name,
      nameLast: memberInfo.last_name,
      dateOfBirth: memberInfo.DOB,
      lineOfBusiness: memberInfo.lineOfBusiness,
      enterpriseIndividual: memberInfo.enterpriseIndividualIdentifier,
      caseId: memberDetails?.case_id ?? null,
      id: memberInfo.member_id,
      isMedSup: memberInfo.medSup ?? false,
      zipCode: zipcode,
      phoneNumber: memberInfo.phone ?? null,
      email: memberInfo.email ?? null,
      referenceId: memberInfo.referenceId|| (memberInfo.member_id&&memberInfo.address?.state?memberInfo.member_id+'-'+memberInfo.address?.state:null),
    },
    program: {
      id: programData?.id,
      name: programData?.document.name,
      organizationName: closestLocation?.name || programData.relationships.provider?.data.document?.name || programData?.document.name,
      organizationId: closestLocation?.id,
      address1: closestLocation?.line_1,
      address2: closestLocation?.line_2,
      city: closestLocation?.city,
      state: closestLocation?.state,
      zipcode: closestLocation?.postal_code,
    },
    need: {
      label: needSelectOptions.find((option) => {return option.value === needValue})?.label || needValue,
      id: needValue,
    },
    contactPreferences: contactPrefs,
    consumerId: memberInfo.ConsumerId ?? '',
    secureRoles: loggedInAdvocate.roles,
    comments: (formData.comment || '') + ":"+(formData.memberPhone ? ' ' + formData.memberPhone : '') + (formData.memberEmail ? ' :' + formData.memberEmail : '') || '',
  };
}
